import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2/highstock'
import styled, { useTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import { getRigTableChartOptions } from './chart-options'
import {
    renderButtons,
    StaticDataType,
    ButtonOptions,
    STATIC_DATA_STATES,
    renderCreationTooltip,
    DeleteConfirmationModal
} from '../chart-components'
import { ChartContainer, ChartTitle } from '../chart-components-style'
import { StaticDataModal } from '../modal-components'
import { deleteSingleEntity, RIG_TABLE_API } from '../../api/api'
import { StaticDataContext } from '../../context/StaticDataContext'
import { RawRigTableEvent } from '../../utils/type'
import { RigTableModal } from './RigTableModal'

require('highcharts/modules/xrange')(Highcharts)

export const RigTableChart = (): ReactElement => {
    // Static Data Context
    const {
        buttonsRef,
        buttonController,
        setButtonController,
        currentChart,
        setAddEventButtonVisible,
        chartType,
        tooltipPosition,
        setTooltiPosition,
        widgetData,
        widgetTimeSpan,
        tryOpenModal,
        refresh,
        reloadData,
        checkBlockedTrackBeforeAction
    } = useContext(StaticDataContext)

    // Chart States
    const [highchartsElement, setHighchartsElement] = useState<Highcharts.Chart | null>(null)
    const [chartLoaded, setChartLoaded] = useState<boolean>(false)
    const [chartHeight, setChartHeight] = useState<number>(0)
    const chartRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    // Modal States
    const [currentModalEvent, setCurrentModalEvent] = useState<RawRigTableEvent>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const closeModal = (): void => setIsModalOpen(false)
    const openModal = (rawEvent: RawRigTableEvent): void => {
        setCurrentModalEvent(rawEvent)
        setRigTableTooltipMessage(null)
        setIsModalOpen(true)
    }

    // Delete Confirmation Modal State
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)
    const openDeleteConfirmationModal = (rawEvent: RawRigTableEvent): void => {
        setCurrentModalEvent(rawEvent)
        setIsDeleteConfirmationModalOpen(true)
    }

    const deleteRigTable = (rigTableId: any) => {
        return deleteSingleEntity(RIG_TABLE_API, rigTableId).then(() => {
            closeDeleteConfirmationModal()
            reloadData()
        })
    }

    function draw(): void {
        try {
            highchartsElement?.destroy()
            setChartLoaded(false)
            setHighchartsElement(
                Highcharts.chart(
                    chartRef?.current,
                    getRigTableChartOptions(
                        { timeSpan: widgetTimeSpan, height: chartHeight },
                        widgetData.rigTableSeries,
                        theme,
                        openModal,
                        tryOpenModal,
                        setAddEventButtonVisible,
                        setTooltiPosition,
                        setRigTableTooltipMessage,
                        checkBlockedTrackBeforeAction
                    ),
                    () => setChartLoaded(true)
                )
            )
        } catch (error) {
            // highchartsElement does not exits.
        }
    }

    useEffect(() => setChartHeight(chartRef.current.getBoundingClientRect().height), [])

    useEffect(() => draw(), [widgetData.rigTableSeries])

    useEffect(() => {
        if (chartLoaded) {
            setTimeout(() => {
                draw()
            }, 300)
        }
    }, [refresh, chartHeight, theme])

    const buttonProps = {
        buttonFunctions: [
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openDeleteConfirmationModal(currentChart?.rawEvent as RawRigTableEvent)
                    setButtonController(false)
                }),
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openModal(currentChart?.rawEvent as RawRigTableEvent)
                    setButtonController(false)
                })
        ],
        customIcons: [ButtonOptions.Delete, ButtonOptions.Confirm]
    }

    const [rigTableTooltipMessage, setRigTableTooltipMessage] = useState(null)

    return (
        <>
            <ChartContainer>
                <StaticDataModal
                    isOpen={isModalOpen}
                    isDouble={false}
                    closeModal={closeModal}
                    content={<RigTableModal event={currentModalEvent} closeModal={closeModal} />}
                />
                <ChartTitle>
                    <span>{i18n(StaticDataType.RIG_TABLE).toUpperCase()}</span>
                </ChartTitle>
                <Chart ref={chartRef} />
                {buttonController && buttonController === STATIC_DATA_STATES.EMPTY_DISABLED ? (
                    <div></div>
                ) : buttonController && buttonController !== STATIC_DATA_STATES.EMPTY ? (
                    chartType === 'rig_table' && renderButtons(buttonsRef, currentChart, buttonProps)
                ) : (
                    chartType === 'rig_table' &&
                    renderCreationTooltip(
                        buttonsRef,
                        currentChart,
                        tooltipPosition,
                        buttonController,
                        rigTableTooltipMessage
                    )
                )}
            </ChartContainer>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                closeModal={closeDeleteConfirmationModal}
                confirmDeletion={() => {
                    deleteRigTable((currentChart?.rawEvent as RawRigTableEvent).id).then(() => {
                        // currentChart.remove()
                        setButtonController(false)
                    })
                }}
            />
        </>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;
    margin-top: -16px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
